<script setup lang="ts" name="TimelineChartGroups">
import BaseCollapsible from '~/components/BaseCollapsible.vue';
import Icon from '~/components/Icon.vue';

import type { ITimelineChartRow } from '~/types';

defineProps<{
  hoveredRowId: string | number | null;
  timelineRows: ITimelineChartRow[];
}>();

const emit = defineEmits<{
  mouseEnter: [string | number];
  mouseLeave: [];
  open: [string | number];
  close: [string | number];
}>();

const onMouseEnter = (isOpen: boolean, id: string | number) => {
  if (!isOpen) emit('mouseEnter', id);
};
const onOpen = (toggle: () => void) => {
  toggle();
  emit('mouseLeave');
};
</script>

<template>
  <div class="bg-white">
    <BaseCollapsible
      v-for="row in timelineRows"
      :key="row.id"
      class="text-nowrap"
      @open="emit('open', row.id)"
      @close="emit('close', row.id)"
    >
      <template #trigger="{ toggle, isOpen }">
        <button
          type="button"
          class="flex h-[72px] w-full gap-x-2 border-b border-b-charcoal-2 p-6"
          :class="{
            'bg-charcoal-1': hoveredRowId === row.id,
            'border-t-2 border-ocean-4 bg-ocean-0': isOpen,
          }"
          @mouseenter="onMouseEnter(isOpen, row.id)"
          @mouseleave="emit('mouseLeave')"
          @click="onOpen(toggle)"
        >
          <Icon
            :icon="isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'"
            class="text-primary-8"
          />
          <p class="text-subtitle-2" :class="{ '!text-ocean-8': isOpen }">
            {{ row.name }}
          </p>
        </button>
      </template>
      <div
        v-for="(bar, index) in row.bars"
        :key="bar.id"
        class="flex h-[50px] items-center pl-10 text-body-1 text-ocean-8"
        :class="{
          'border-b-2 border-ocean-4': index === row.bars.length - 1,
          'border-b border-charcoal-2': index !== row.bars.length - 1,
          'bg-charcoal-1': hoveredRowId === `${row.id}-${bar.id}`,
        }"
        @mouseenter="emit('mouseEnter', `${row.id}-${bar.id}`)"
        @mouseleave="emit('mouseLeave')"
      >
        {{ bar.details.name }}
      </div>
    </BaseCollapsible>
  </div>
</template>
